"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AGENTS_DEMO_ORG,
  type AgentsDemoOrg,
  type AgentsHumanRoleId,
  type OrgAgent,
  type AgentStatus,
  agentDirectory,
  connectionKindLabel,
  isMailConnection,
  newAgentId,
  newConnectionId,
  newScheduleId,
  newSharedInboxId,
  normalizeEmailCapabilities,
  normalizeMailboxAddress,
  possibleEmailCapabilitiesForProvider,
  DEFAULT_AGENT_EMAIL_CAPABILITIES,
  type AgentEmailCapabilities,
  type AgentEmailMode,
  type AgentMailProvider,
  type ConnectionKind,
  type MailboxSource,
  type OrgConnection,
  type OrgSchedule,
  type OrgSharedInbox,
  type ScheduleCadence,
} from "@/lib/agentsDemoData";
import { isAgentMailProvider } from "@/lib/agentPersonalMail";
import {
  can,
  isViewOnly,
  primaryRoleLabel,
  type AgentsPermission,
} from "@/lib/agentsAccess";
import { useSession } from "@/auth/SessionProvider";
import {
  buildPersistedSnapshot,
  clearAgentsPersistedSnapshot,
  hasAttemptedRosterImport,
  loadAgentsPersistedSnapshot,
  localSnapshotHasHireData,
  markRosterImportAttempted,
  mergeOrgWithPersisted,
  saveAgentsPersistedSnapshot,
  snapshotToRosterWrite,
  type AgentsViewerMode,
} from "@/lib/agentsPersistence";
import {
  ensureAgentsOrgContext,
  peekAgentsOrgContext,
  resetAgentsOrgContextCache,
} from "@/lib/ensureAgentsOrgContext";
import { writeDashboardOrgCookie } from "@/lib/dashboardOrg";
import {
  fetchWorkforceRoster,
  importWorkforceRoster,
  saveWorkforceRoster,
  type WorkforceRosterPayload,
} from "@/lib/workforceRosterApi";

export type { AgentsViewerMode };

export type AgentsOrgContextStatus = "loading" | "ready" | "missing";

type AgentsContextValue = {
  org: AgentsDemoOrg;
  /** Demo toggle: Super Admin vs Standard (view-only). */
  viewerMode: AgentsViewerMode;
  setViewerMode: (mode: AgentsViewerMode) => void;
  viewerRoles: AgentsHumanRoleId[];
  viewerLabel: string;
  viewOnly: boolean;
  may: (permission: AgentsPermission) => boolean;
  archiveAgent: (agentId: string) => void;
  setAgentStatus: (agentId: string, status: Exclude<AgentStatus, "archived">) => void;
  duplicateAgent: (agentId: string) => string | null;
  hireAgent: (input: {
    name: string;
    jobSummary: string;
    ownerUserId: string;
    sharedWithUserIds: string[];
  }) => string | null;
  addConnection: (input: { name: string; kind: ConnectionKind; mail?: boolean }) => string | null;
  addSchedule: (input: { name: string; cadence: ScheduleCadence; note?: string }) => string | null;
  addSharedInbox: (input: {
    name: string;
    emailAddress: string;
    connectionId: string;
    note?: string;
  }) => string | null;
  setAgentSchedules: (agentId: string, scheduleIds: string[]) => boolean;
  setAgentMailbox: (
    agentId: string,
    input: { mailboxAddress: string; mailboxSource: MailboxSource },
  ) => boolean;
  /**
   * Start agent-owned personal mail connect for a provider.
   * Sets pending (never fake connected), flushes roster, then caller may OAuth-redirect.
   */
  startAgentPersonalMailConnect: (
    agentId: string,
    provider: AgentMailProvider,
  ) => Promise<boolean>;
  /**
   * Mark agent personal mail connected after a successful OAuth / ATW session callback.
   * Stores only non-secret metadata (provider + mailbox). Tokens stay worker-side.
   * Flushes roster so hydration cannot resurrect a stale pending row.
   */
  completeAgentPersonalMailConnect: (
    agentId: string,
    input: { provider: AgentMailProvider; mailboxAddress: string },
  ) => Promise<boolean>;
  /** Cancel pending / clear personal mail provider back to disconnected. */
  clearAgentPersonalMail: (agentId: string) => Promise<boolean>;
  setAgentEmailMode: (agentId: string, emailMode: AgentEmailMode) => boolean;
  setAgentSharedInboxes: (agentId: string, sharedInboxIds: string[]) => boolean;
  setAgentPersonalEmailCapabilities: (
    agentId: string,
    capabilities: AgentEmailCapabilities,
  ) => boolean;
  setAgentSharedInboxCapabilities: (
    agentId: string,
    inboxId: string,
    capabilities: AgentEmailCapabilities,
  ) => boolean;
  setConnectionStatus: (connectionId: string, status: OrgConnection["status"]) => void;
  getAgent: (agentId: string) => OrgAgent | undefined;
  persistenceReady: boolean;
  /** Where the live roster is stored after hydration. */
  persistenceSource: "server" | "local" | "loading";
  persistenceError: string | null;
  resetLocalDemo: () => void;
  /** Session / cookie / profile org resolved for suite APIs (knowledge, etc.). */
  orgContextStatus: AgentsOrgContextStatus;
  orgContextError: string | null;
  /** Stamp dashboard-org and update local org context without touching SSO session cookies. */
  applyActiveOrg: (orgId: string, companyName?: string | null) => void;
};

const AgentsContext = createContext<AgentsContextValue | null>(null);

function applyRosterToOrg(base: AgentsDemoOrg, roster: WorkforceRosterPayload): AgentsDemoOrg {
  return {
    ...base,
    orgId: roster.orgId || base.orgId,
    agents: roster.agents,
    connections: roster.connections,
    schedules: roster.schedules,
    sharedInboxes: roster.sharedInboxes,
    maxAddon: roster.maxAddon,
  };
}

export function AgentsContextProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [org, setOrg] = useState<AgentsDemoOrg>(() =>
    mergeOrgWithPersisted(AGENTS_DEMO_ORG, loadAgentsPersistedSnapshot()),
  );
  const [viewerMode, setViewerModeState] = useState<AgentsViewerMode>(() => {
    const snap = loadAgentsPersistedSnapshot();
    return snap?.viewerMode ?? "super_admin";
  });
  const [persistenceReady, setPersistenceReady] = useState(false);
  const [persistenceSource, setPersistenceSource] = useState<"server" | "local" | "loading">(
    "loading",
  );
  const [persistenceError, setPersistenceError] = useState<string | null>(null);
  const [orgContextStatus, setOrgContextStatus] = useState<AgentsOrgContextStatus>("loading");
  const [orgContextError, setOrgContextError] = useState<string | null>(null);
  const [rosterHydrationKey, setRosterHydrationKey] = useState(0);
  /** Always-current org for sync personal-mail mutations (setState updaters can defer). */
  const orgRef = useRef(org);
  orgRef.current = org;
  /** Bumps when a personal-mail flush owns the next roster PUT (skip stale debounce). */
  const personalMailFlushGenRef = useRef(0);

  const setViewerMode = useCallback((mode: AgentsViewerMode) => {
    setViewerModeState(mode);
  }, []);

  useEffect(() => {
    if (session.status !== "authenticated") {
      resetAgentsOrgContextCache();
      setOrgContextStatus(session.status === "loading" ? "loading" : "missing");
      setOrgContextError(null);
      setPersistenceReady(session.status !== "loading");
      setPersistenceSource(session.status === "loading" ? "loading" : "local");
      return;
    }

    let cancelled = false;
    if (!peekAgentsOrgContext()?.orgId) {
      setOrgContextStatus("loading");
      setOrgContextError(null);
    }
    void (async () => {
      try {
        const result = await ensureAgentsOrgContext(session.data?.user?.orgId);
        if (cancelled) return;
        if (result.orgId) {
          setOrg((prev) => ({
            ...prev,
            name: result.companyName || prev.name,
            orgId: result.orgId || prev.orgId,
          }));
          setOrgContextStatus("ready");
          setOrgContextError(null);
        } else {
          setOrgContextStatus("missing");
          setOrgContextError(result.error || "Organization context required.");
          setPersistenceReady(true);
          setPersistenceSource("local");
        }
      } catch (e) {
        if (cancelled) return;
        setOrgContextStatus("missing");
        setOrgContextError(e instanceof Error ? e.message : "Could not resolve organization.");
        setPersistenceReady(true);
        setPersistenceSource("local");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.status, session.data?.user?.orgId]);

  useEffect(() => {
    if (session.status !== "authenticated" || orgContextStatus !== "ready" || !org.orgId) {
      return;
    }

    let cancelled = false;
    setPersistenceReady(false);
    setPersistenceSource("loading");
    setPersistenceError(null);

    void (async () => {
      const localSnap = loadAgentsPersistedSnapshot();
      const loaded = await fetchWorkforceRoster();
      if (cancelled) return;

      if (!loaded.ok) {
        setOrg(mergeOrgWithPersisted(AGENTS_DEMO_ORG, localSnap));
        setPersistenceSource("local");
        setPersistenceError(loaded.error);
        setPersistenceReady(true);
        return;
      }

      let roster = loaded.roster;
      if (
        roster.empty &&
        localSnapshotHasHireData(localSnap) &&
        !hasAttemptedRosterImport(org.orgId)
      ) {
        markRosterImportAttempted(org.orgId);
        const imported = await importWorkforceRoster(snapshotToRosterWrite(localSnap!));
        if (cancelled) return;
        if (imported.ok) {
          roster = imported.roster;
        } else if (imported.roster) {
          roster = imported.roster;
        }
      }

      setOrg((prev) =>
        applyRosterToOrg(
          {
            ...AGENTS_DEMO_ORG,
            name: prev.name || AGENTS_DEMO_ORG.name,
            orgId: roster.orgId || prev.orgId,
          },
          roster,
        ),
      );
      setPersistenceSource("server");
      setPersistenceError(null);
      setPersistenceReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [session.status, orgContextStatus, org.orgId, rosterHydrationKey]);

  useEffect(() => {
    if (!persistenceReady) return;
    // Keep a browser cache for one-time import / offline paint. Server is authoritative when ready.
    saveAgentsPersistedSnapshot(buildPersistedSnapshot(org, viewerMode));
  }, [org, viewerMode, persistenceReady]);

  useEffect(() => {
    if (!persistenceReady || persistenceSource !== "server" || !org.orgId) return;
    const genAtSchedule = personalMailFlushGenRef.current;
    const handle = window.setTimeout(() => {
      // Personal-mail connect/clear already flushed; do not PUT a stale pre-flush snapshot.
      if (personalMailFlushGenRef.current !== genAtSchedule) return;
      void saveWorkforceRoster({
        agents: org.agents,
        connections: org.connections,
        schedules: org.schedules,
        sharedInboxes: org.sharedInboxes,
        maxAddon: org.maxAddon,
      }).then((result) => {
        if (!result.ok) {
          setPersistenceError(result.error);
          return;
        }
        setPersistenceError(null);
      });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [
    persistenceReady,
    persistenceSource,
    org.orgId,
    org.agents,
    org.connections,
    org.schedules,
    org.sharedInboxes,
    org.maxAddon,
  ]);

  const viewerRoles: AgentsHumanRoleId[] = useMemo(
    () => (viewerMode === "super_admin" ? ["super_admin", "standard"] : ["standard"]),
    [viewerMode],
  );

  const may = useCallback(
    (permission: AgentsPermission) => can(viewerRoles, permission),
    [viewerRoles],
  );

  const archiveAgent = useCallback(
    (agentId: string) => {
      if (!can(viewerRoles, "create_edit_archive_agents")) return;
      setOrg((prev) => ({
        ...prev,
        agents: prev.agents.map((agent) =>
          agent.id === agentId && agent.status !== "archived"
            ? ({ ...agent, status: "archived" as AgentStatus })
            : agent,
        ),
      }));
    },
    [viewerRoles],
  );

  const setAgentStatus = useCallback(
    (agentId: string, status: Exclude<AgentStatus, "archived">) => {
      if (!can(viewerRoles, "create_edit_archive_agents")) return;
      setOrg((prev) => ({
        ...prev,
        agents: prev.agents.map((agent) => {
          if (agent.id !== agentId || agent.status === "archived") return agent;
          return { ...agent, status };
        }),
      }));
    },
    [viewerRoles],
  );

  const duplicateAgent = useCallback(
    (agentId: string) => {
      if (!can(viewerRoles, "create_edit_archive_agents")) return null;
      const source = org.agents.find((agent) => agent.id === agentId);
      if (!source) return null;
      const baseName = source.name.replace(/\s*\(copy(?:\s+\d+)?\)\s*$/i, "").trim() || source.name;
      const existingNames = new Set(org.agents.map((agent) => agent.name.toLowerCase()));
      let name = `${baseName} (copy)`;
      let n = 2;
      while (existingNames.has(name.toLowerCase())) {
        name = `${baseName} (copy ${n})`;
        n += 1;
      }
      const id = newAgentId(name);
      const copy: OrgAgent = {
        id,
        name,
        status: "active",
        humanOwnerUserId: source.humanOwnerUserId,
        humanOwnerName: source.humanOwnerName,
        humanOwnerEmail: source.humanOwnerEmail,
        sharedWithUserIds: [...(source.sharedWithUserIds ?? [])],
        mailboxAddress: null,
        mailboxSource: null,
        personalMailProvider: null,
        personalMailStatus: "disconnected",
        personalMailConnected: false,
        emailMode: null,
        sharedInboxIds: [],
        scheduleIds: [...(source.scheduleIds ?? [])],
        lastActiveAt: new Date().toISOString(),
        creditsBurnedMonth: 0,
        ...(source.jobSummary ? { jobSummary: source.jobSummary } : {}),
        ...(source.agentType ? { agentType: source.agentType } : {}),
        ...(source.agentTypeLabel ? { agentTypeLabel: source.agentTypeLabel } : {}),
      };
      setOrg((prev) => ({
        ...prev,
        agents: [copy, ...prev.agents],
      }));
      return id;
    },
    [viewerRoles, org.agents],
  );

  const hireAgent = useCallback(
    (input: {
      name: string;
      jobSummary: string;
      ownerUserId: string;
      sharedWithUserIds: string[];
    }) => {
      if (!can(viewerRoles, "create_edit_archive_agents")) return null;
      const name = input.name.trim();
      const jobSummary = input.jobSummary.trim();
      const people = agentDirectory(org.users, session.data?.user);
      const owner = people.find((person) => person.id === input.ownerUserId);
      if (!name || !jobSummary || !owner) return null;

      const sharedWithUserIds = [...new Set(input.sharedWithUserIds)].filter(
        (id) => id !== owner.id && people.some((person) => person.id === id),
      );
      const id = newAgentId(name);
      const hired: OrgAgent = {
        id,
        name,
        jobSummary,
        status: "active",
        humanOwnerUserId: owner.id,
        humanOwnerName: owner.name,
        humanOwnerEmail: owner.email,
        sharedWithUserIds,
        mailboxAddress: null,
        mailboxSource: null,
        personalMailProvider: null,
        personalMailStatus: "disconnected",
        personalMailConnected: false,
        emailMode: null,
        sharedInboxIds: [],
        scheduleIds: [],
        lastActiveAt: new Date().toISOString(),
        creditsBurnedMonth: 0,
      };
      setOrg((prev) => ({
        ...prev,
        agents: [hired, ...prev.agents],
      }));
      return id;
    },
    [viewerRoles, org.users, session.data?.user],
  );

  const addConnection = useCallback(
    (input: { name: string; kind: ConnectionKind; mail?: boolean }) => {
      if (!can(viewerRoles, "manage_connections")) return null;
      const name = input.name.trim();
      if (!name) return null;

      const id = newConnectionId(name);
      const added: OrgConnection = {
        id,
        name,
        kind: input.kind,
        provider: connectionKindLabel(input.kind),
        status: "not_connected",
        note: "",
        ...(input.mail ? { mail: true } : {}),
      };
      setOrg((prev) => ({
        ...prev,
        connections: [added, ...prev.connections],
      }));
      return id;
    },
    [viewerRoles],
  );

  const addSchedule = useCallback(
    (input: { name: string; cadence: ScheduleCadence; note?: string }) => {
      if (!can(viewerRoles, "manage_schedules")) return null;
      const name = input.name.trim();
      if (!name) return null;
      const note = input.note?.trim() ?? "";
      const id = newScheduleId(name);
      const added: OrgSchedule = {
        id,
        name,
        cadence: input.cadence,
        ...(note ? { note } : {}),
      };
      setOrg((prev) => ({
        ...prev,
        schedules: [added, ...prev.schedules],
      }));
      return id;
    },
    [viewerRoles],
  );

  const setAgentSchedules = useCallback(
    (agentId: string, scheduleIds: string[]) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      setOrg((prev) => {
        if (!prev.agents.some((agent) => agent.id === agentId)) return prev;
        const allowed = new Set(prev.schedules.map((schedule) => schedule.id));
        const next = [...new Set(scheduleIds)].filter((id) => allowed.has(id));
        return {
          ...prev,
          agents: prev.agents.map((agent) =>
            agent.id === agentId ? { ...agent, scheduleIds: next } : agent,
          ),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const addSharedInbox = useCallback(
    (input: {
      name: string;
      emailAddress: string;
      connectionId: string;
      note?: string;
    }) => {
      if (!can(viewerRoles, "manage_connections")) return null;
      const name = input.name.trim();
      const emailAddress = normalizeMailboxAddress(input.emailAddress);
      const connectionId = input.connectionId.trim();
      if (!name || !emailAddress || !connectionId) return null;
      const connection = org.connections.find((conn) => conn.id === connectionId);
      if (!connection || !isMailConnection(connection)) return null;
      const note = input.note?.trim() ?? "";
      const id = newSharedInboxId(name);
      const added: OrgSharedInbox = {
        id,
        name,
        emailAddress,
        connectionId,
        ...(note ? { note } : {}),
      };
      setOrg((prev) => ({
        ...prev,
        sharedInboxes: [added, ...prev.sharedInboxes],
      }));
      return id;
    },
    [viewerRoles, org.connections],
  );

  const setAgentMailbox = useCallback(
    (agentId: string, input: { mailboxAddress: string; mailboxSource: MailboxSource }) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      const mailboxAddress = normalizeMailboxAddress(input.mailboxAddress);
      if (!mailboxAddress) return false;
      setOrg((prev) => {
        if (!prev.agents.some((agent) => agent.id === agentId)) return prev;
        return {
          ...prev,
          agents: prev.agents.map((agent) =>
            agent.id === agentId
              ? {
                  ...agent,
                  mailboxAddress,
                  mailboxSource: input.mailboxSource,
                  // Address alone is not OAuth success. Keep status as-is.
                  personalEmailCapabilities: normalizeEmailCapabilities(
                    agent.personalEmailCapabilities,
                  ),
                }
              : agent,
          ),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const flushPersonalMailOrg = useCallback(
    async (nextOrg: AgentsDemoOrg) => {
      personalMailFlushGenRef.current += 1;
      saveAgentsPersistedSnapshot(buildPersistedSnapshot(nextOrg, viewerMode));
      if (persistenceSource !== "server") return true;
      const result = await saveWorkforceRoster({
        agents: nextOrg.agents,
        connections: nextOrg.connections,
        schedules: nextOrg.schedules,
        sharedInboxes: nextOrg.sharedInboxes,
        maxAddon: nextOrg.maxAddon,
      });
      if (!result.ok) {
        setPersistenceError(result.error);
        return false;
      }
      setPersistenceError(null);
      return true;
    },
    [persistenceSource, viewerMode],
  );

  const startAgentPersonalMailConnect = useCallback(
    async (agentId: string, provider: AgentMailProvider) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      if (!isAgentMailProvider(provider)) return false;
      const prev = orgRef.current;
      if (!prev.agents.some((agent) => agent.id === agentId)) return false;
      const nextOrg: AgentsDemoOrg = {
        ...prev,
        agents: prev.agents.map((agent) =>
          agent.id === agentId
            ? {
                ...agent,
                personalMailProvider: provider,
                personalMailStatus: "pending",
                personalMailConnected: false,
                personalEmailCapabilities: normalizeEmailCapabilities(
                  agent.personalEmailCapabilities,
                ),
              }
            : agent,
        ),
      };
      orgRef.current = nextOrg;
      setOrg(nextOrg);
      // Flush before OAuth redirect so D1 does not still hold disconnected after return.
      const saved = await flushPersonalMailOrg(nextOrg);
      if (!saved) {
        orgRef.current = prev;
        setOrg(prev);
        return false;
      }
      return true;
    },
    [viewerRoles, flushPersonalMailOrg],
  );

  const completeAgentPersonalMailConnect = useCallback(
    async (agentId: string, input: { provider: AgentMailProvider; mailboxAddress: string }) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      if (!isAgentMailProvider(input.provider)) return false;
      const mailboxAddress = normalizeMailboxAddress(input.mailboxAddress);
      if (!mailboxAddress) return false;
      const prev = orgRef.current;
      if (!prev.agents.some((agent) => agent.id === agentId)) return false;
      const nextOrg: AgentsDemoOrg = {
        ...prev,
        agents: prev.agents.map((agent) =>
          agent.id === agentId
            ? {
                ...agent,
                personalMailProvider: input.provider,
                personalMailStatus: "connected",
                personalMailConnected: true,
                mailboxAddress,
                mailboxSource: agent.mailboxSource ?? "existing",
                // Initial caps = max possible for this provider's scopes.
                // User may turn individual caps off later as policy.
                personalEmailCapabilities: possibleEmailCapabilitiesForProvider(
                  input.provider,
                ),
              }
            : agent,
        ),
      };
      orgRef.current = nextOrg;
      setOrg(nextOrg);
      // Flush immediately so a later hydration/debounce cannot resurrect pending.
      const saved = await flushPersonalMailOrg(nextOrg);
      if (!saved) {
        orgRef.current = prev;
        setOrg(prev);
        return false;
      }
      return true;
    },
    [viewerRoles, flushPersonalMailOrg],
  );

  const clearAgentPersonalMail = useCallback(
    async (agentId: string) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      const prev = orgRef.current;
      if (!prev.agents.some((agent) => agent.id === agentId)) return false;
      const nextOrg: AgentsDemoOrg = {
        ...prev,
        agents: prev.agents.map((agent) =>
          agent.id === agentId
            ? {
                ...agent,
                personalMailProvider: null,
                personalMailStatus: "disconnected",
                personalMailConnected: false,
                mailboxAddress: null,
                mailboxSource: null,
                personalEmailCapabilities: { ...DEFAULT_AGENT_EMAIL_CAPABILITIES },
              }
            : agent,
        ),
      };
      orgRef.current = nextOrg;
      setOrg(nextOrg);
      const saved = await flushPersonalMailOrg(nextOrg);
      if (!saved) {
        orgRef.current = prev;
        setOrg(prev);
        return false;
      }
      return true;
    },
    [viewerRoles, flushPersonalMailOrg],
  );

  const setAgentEmailMode = useCallback(
    (agentId: string, emailMode: AgentEmailMode) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      setOrg((prev) => {
        if (!prev.agents.some((agent) => agent.id === agentId)) return prev;
        return {
          ...prev,
          agents: prev.agents.map((agent) =>
            agent.id === agentId ? { ...agent, emailMode } : agent,
          ),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const setAgentSharedInboxes = useCallback(
    (agentId: string, sharedInboxIds: string[]) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      setOrg((prev) => {
        if (!prev.agents.some((agent) => agent.id === agentId)) return prev;
        const allowed = new Set(prev.sharedInboxes.map((inbox) => inbox.id));
        const nextIds = [...new Set(sharedInboxIds)].filter((id) => allowed.has(id));
        return {
          ...prev,
          agents: prev.agents.map((agent) => {
            if (agent.id !== agentId) return agent;
            const caps = { ...(agent.sharedInboxCapabilities ?? {}) };
            for (const id of Object.keys(caps)) {
              if (!nextIds.includes(id)) delete caps[id];
            }
            for (const id of nextIds) {
              if (!caps[id]) caps[id] = normalizeEmailCapabilities(null);
            }
            return {
              ...agent,
              sharedInboxIds: nextIds,
              sharedInboxCapabilities: caps,
            };
          }),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const setAgentPersonalEmailCapabilities = useCallback(
    (agentId: string, capabilities: AgentEmailCapabilities) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      const next = normalizeEmailCapabilities(capabilities);
      setOrg((prev) => {
        if (!prev.agents.some((agent) => agent.id === agentId)) return prev;
        return {
          ...prev,
          agents: prev.agents.map((agent) =>
            agent.id === agentId ? { ...agent, personalEmailCapabilities: next } : agent,
          ),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const setAgentSharedInboxCapabilities = useCallback(
    (agentId: string, inboxId: string, capabilities: AgentEmailCapabilities) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      const next = normalizeEmailCapabilities(capabilities);
      setOrg((prev) => {
        const agent = prev.agents.find((row) => row.id === agentId);
        if (!agent) return prev;
        if (!(agent.sharedInboxIds ?? []).includes(inboxId)) return prev;
        return {
          ...prev,
          agents: prev.agents.map((row) =>
            row.id === agentId
              ? {
                  ...row,
                  sharedInboxCapabilities: {
                    ...(row.sharedInboxCapabilities ?? {}),
                    [inboxId]: next,
                  },
                }
              : row,
          ),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const setConnectionStatus = useCallback(
    (connectionId: string, status: OrgConnection["status"]) => {
      if (!can(viewerRoles, "manage_connections")) return;
      setOrg((prev) => ({
        ...prev,
        connections: prev.connections.map((c) =>
          c.id === connectionId ? { ...c, status } : c,
        ),
      }));
    },
    [viewerRoles],
  );

  const resetLocalDemo = useCallback(() => {
    // Never wipe the org roster in D1. Clear the browser cache and reload from the server.
    clearAgentsPersistedSnapshot();
    setViewerModeState("super_admin");
    setRosterHydrationKey((n) => n + 1);
  }, []);

  const applyActiveOrg = useCallback((orgId: string, companyName?: string | null) => {
    const id = orgId.trim();
    if (!id) return;
    writeDashboardOrgCookie(id);
    resetAgentsOrgContextCache();
    setPersistenceReady(false);
    setPersistenceSource("loading");
    setOrg((prev) => ({
      ...prev,
      orgId: id,
      name: companyName?.trim() || prev.name,
      agents: [],
      connections: [],
      schedules: [],
      sharedInboxes: [],
    }));
    setOrgContextStatus("ready");
    setOrgContextError(null);
    setRosterHydrationKey((n) => n + 1);
  }, []);

  const getAgent = useCallback(
    (agentId: string) => org.agents.find((agent) => agent.id === agentId),
    [org.agents],
  );

  const value: AgentsContextValue = {
    org,
    viewerMode,
    setViewerMode,
    viewerRoles,
    viewerLabel: primaryRoleLabel(viewerRoles),
    viewOnly: isViewOnly(viewerRoles),
    may,
    archiveAgent,
    setAgentStatus,
    duplicateAgent,
    hireAgent,
    addConnection,
    addSchedule,
    addSharedInbox,
    setAgentSchedules,
    setAgentMailbox,
    startAgentPersonalMailConnect,
    completeAgentPersonalMailConnect,
    clearAgentPersonalMail,
    setAgentEmailMode,
    setAgentSharedInboxes,
    setAgentPersonalEmailCapabilities,
    setAgentSharedInboxCapabilities,
    setConnectionStatus,
    getAgent,
    persistenceReady,
    persistenceSource,
    persistenceError,
    resetLocalDemo,
    orgContextStatus,
    orgContextError,
    applyActiveOrg,
  };

  return <AgentsContext.Provider value={value}>{children}</AgentsContext.Provider>;
}

export function useAgents(): AgentsContextValue {
  const ctx = useContext(AgentsContext);
  if (!ctx) throw new Error("useAgents must be used within AgentsContextProvider");
  return ctx;
}
